/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";

/**
 * Bounded record of recently seen notification correlation ids.
 *
 * The server may retransmit a notification with the same `Origin-RequestId`;
 * this cache lets PushNotificationModelImpl drop the repeat before any
 * feature model is re-entered.
 *
 * Both bounds are enforced, so the cache cannot grow without limit:
 * - MAX_ENTRIES caps the size, evicting oldest-first.
 * - TTL_MS expires entries that can no longer be a meaningful retransmit.
 *
 * A duplicate hit deliberately does NOT refresh the entry's timestamp
 * (FIFO, not LRU) so a stream of repeats cannot extend the window forever.
 */
export class PushNotificationDedupeCache {
  private static readonly TAG: string = "PushNotificationDedupeCache";

  /** Hard size ceiling — far above any plausible burst, negligible memory. */
  private static readonly MAX_ENTRIES: number = 64;

  /** Age past which an entry can no longer be a meaningful retransmit. */
  private static readonly TTL_MS: number = 600_000; // 10 minutes

  /**
   * Correlation id to insertion time in epoch milliseconds.
   *
   * A Map iterates in insertion order, so iteration order is age order and
   * the head is always the oldest entry.
   */
  private readonly mSeen: Map<string, number> = new Map<string, number>();

  /**
   * Test whether a key has already been seen, recording it when it has not.
   *
   * @param key - The correlation id to test
   * @param nowMs - Current time in epoch milliseconds
   * @returns True when the key was already recorded and is still live
   */
  public isDuplicate(key: string, nowMs: number): boolean {
    this.evictExpired(nowMs);

    if (this.mSeen.has(key)) {
      return true;
    }

    this.mSeen.set(key, nowMs);

    while (this.mSeen.size > PushNotificationDedupeCache.MAX_ENTRIES) {
      this.evictOldest();
    }

    return false;
  }

  /**
   * Forget every recorded key.
   */
  public clear(): void {
    this.mSeen.clear();
  }

  /**
   * Current number of recorded keys. Exposed for tests and diagnostics.
   *
   * @returns The entry count
   */
  public getSize(): number {
    return this.mSeen.size;
  }

  /**
   * Drop entries older than TTL_MS.
   *
   * Walks from the head of the insertion-ordered map and stops at the first
   * live entry, so the cost is proportional to the number evicted rather
   * than to the cache size.
   *
   * @param nowMs - Current time in epoch milliseconds
   */
  private evictExpired(nowMs: number): void {
    for (const [key, insertedAtMs] of this.mSeen) {
      if (nowMs - insertedAtMs <= PushNotificationDedupeCache.TTL_MS) {
        return;
      }
      this.mSeen.delete(key);
    }
  }

  /**
   * Drop the single oldest entry.
   */
  private evictOldest(): void {
    const oldest: string | undefined = this.mSeen.keys().next().value;
    if (oldest === undefined) {
      return;
    }
    this.mSeen.delete(oldest);
    Log.d(
      PushNotificationDedupeCache.TAG,
      `evictOldest(): Evicted ${oldest} — size limit reached`
    );
  }
}
